import React, { useCallback, useEffect, useRef, useSyncExternalStore } from "react"
import { closeOverlay, openOverlay, useOverlayDispatch, useOverlayState } from "../overlay"

import ReAuth from "../../components/forms/re-auth"
import { SDK } from "../../sdk"
import { TState } from "../../sdk/state"

const RE_AUTH_Z_INDEX = 13

const APIClientContext = React.createContext<SDK | undefined>(undefined)

const useSDK = () => {
    const client = React.useContext(APIClientContext)
    if (client === undefined) {
        throw new Error("useSDK must be used within a APIClientProvider")
    }

    return client
}

// subscribes to one value of the sdk state. the selector has to return a primitive: a new object
// on every call would make useSyncExternalStore re-render forever
const useAPIState = <T,>(selector: (state: TState) => T) => {
    const client = useSDK()

    return useSyncExternalStore(
        client.state.subscribe,
        () => selector(client.state.getSnapshot()),
        () => selector(client.state.getServerSnapshot())
    )
}

type APIClientProviderProps = {
    client: SDK
    children: React.ReactNode
}

const APIClientProvider = ({ client, children }: APIClientProviderProps) => {
    // another tab or another subdomain can end or start a session without us noticing. the storage
    // event covers the other tabs, coming back to this one covers the cookies
    useEffect(() => {
        window.addEventListener("storage", client.state.sync)
        document.addEventListener("visibilitychange", client.state.sync)

        return () => {
            window.removeEventListener("storage", client.state.sync)
            document.removeEventListener("visibilitychange", client.state.sync)
        }
    }, [client])

    const overlayDispatch = useOverlayDispatch()
    const { showOverlay, content } = useOverlayState()

    // the retry policy itself lives in the sdk, which owns the token. this only tells it when it is
    // worth trying again immediately instead of waiting out the current delay
    useEffect(() => {
        const recover = () => {
            if (document.visibilityState !== "visible") return

            client.retryConsumerAuth()
        }

        window.addEventListener("online", recover)
        document.addEventListener("visibilitychange", recover)

        return () => {
            window.removeEventListener("online", recover)
            document.removeEventListener("visibilitychange", recover)
        }
    }, [client])

    const reAuthRef = useRef<{
        content: React.ReactNode
        resolve: () => void
        reject: (reason?: unknown) => void
    } | null>(null)

    const handleSuccess = useCallback(() => {
        const pending = reAuthRef.current
        reAuthRef.current = null

        pending?.resolve()
        closeOverlay(overlayDispatch)
    }, [overlayDispatch])

    useEffect(() => {
        const pending = reAuthRef.current
        if (!pending || (showOverlay && content === pending.content)) return

        reAuthRef.current = null
        pending.reject(undefined)
    }, [showOverlay, content])

    useEffect(() => {
        client.mount([
            {
                hookKey: "reAuth",
                hookFn: () =>
                    new Promise<void>((resolve, reject) => {
                        const reAuthContent = <ReAuth onSuccess={handleSuccess} />

                        reAuthRef.current = { content: reAuthContent, resolve, reject }
                        openOverlay(overlayDispatch, reAuthContent, { zIndex: RE_AUTH_Z_INDEX })
                    }),
            },
        ])

        return () => {
            client.unmount()

            const pending = reAuthRef.current
            if (!pending) return

            reAuthRef.current = null
            pending.reject(undefined)
        }
    }, [client, overlayDispatch, handleSuccess])

    return <APIClientContext.Provider value={client}>{children}</APIClientContext.Provider>
}

export { APIClientProvider, useAPIState, useSDK }
